/**
 * Lazy-load reCAPTCHA v3 and return a token.
 * Script is only fetched when a form actually needs it (not on every page load).
 * Returns null if reCAPTCHA is not configured (dev environment).
 */

let loadPromise = null;

function loadRecaptchaScript() {
  if (window.grecaptcha) return Promise.resolve();
  if (loadPromise) return loadPromise;

  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (!siteKey) return Promise.resolve();

  loadPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return loadPromise;
}

export const getRecaptchaToken = async (action = 'submit') => {
  await loadRecaptchaScript();

  if (!window.grecaptcha) return null;

  try {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) return null;

    // grecaptcha exists as soon as the script's onload fires, but execute()
    // is NOT safe to call until grecaptcha.ready() resolves. Without this
    // wait, the very first submit after a fresh page load can return a null
    // token — the "reCAPTCHA verification required" error a real user hits
    // on their first click. Waiting for ready() makes the first call reliable.
    await new Promise((resolve) => window.grecaptcha.ready(resolve));

    const token = await window.grecaptcha.execute(siteKey, { action });
    return token;
  } catch (error) {
    console.error('reCAPTCHA error:', error);
    return null;
  }
};
